<template>
    <div class="pt-[20px] bg-[#ededed]">
        <div class="max-w-[1400px] w-full container mx-auto">
            <div class="pt-[60px] pr-[20px] pb-[2%] pl-[10px] max-w-[1400px]">
                <offerNavigation />
            </div>
        </div>
    </div>
    <div class="max-w-[1400px] w-full container mx-auto">
        <v-breadcrumbs :items="breadcrumbs"></v-breadcrumbs>
        <div class="pt-[30px] pr-[20px] pb-[40px] pl-[10px] max-w-[1400px] text-center">
            <h2 class="text-left text-2xl font-light mb-[3%]">
                <strong>Kontakt für Offerte auswählen</strong>
            </h2>
            <v-card class="w-full mx-auto" color="grey-lighten-3">
                <v-card-text>
                    <v-text-field
                        :loading="loading"
                        density="comfortable"
                        type="input"
                        @input="search"
                        variant="solo"
                        label="Bitte Schule auswählen"
                        title="Schule eingeben"
                        autofocus
                        v-model="searchTerm"
                        append-inner-icon="mdi-magnify"
                        single-line
                        hide-details
                    ></v-text-field>
                </v-card-text>
            </v-card>
            <ContactsTable
                :load="load"
                :hasActions="true"
                :contacts="contacts"
                :selectMethod="selectContact"
                :redirectTo="'/offers/createoffer/'"
                :duplicateMethod="duplicateContact"
                :contactsCount="contactsCount"
                :deleteMethod="deleteContact"
            />
            <hr />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, computed, onMounted } from 'vue'
    import offerNavigation from '@/components/SubNavigations/TheOfferNavigation.vue'
    import ContactsTable from '@/components/Contacts/ContactsTable.vue'
    import useContactStore from '@/utils/useContactStore'
    import useOfferStore from '@/utils/useOfferStore'
    import router from '@/router'
    import { Offer } from '@/model/types'

    // Retrieve contact store functions and reactive properties.
    const {
        contacts,
        loading,
        fetchContacts,
        fetchContactsSearch,
        duplicateContact,
        deleteContact,
    } = useContactStore()
    // We also retrieve our HATEOAS-related functions from the offer store.
    const { getHateoasLinks, hasLink } = useOfferStore()

    const searchTerm = ref('')

    // On mount, load the HATEOAS links (if not already available) and the contacts.
    onMounted(async () => {
        await getHateoasLinks() // Assumes this populates internal HATEOAS link cache
        await fetchContacts({ limit: 20, offset: 0, filter: '' })
    })

    // Determine if the current user is restricted (i.e. should see only a few contacts)
    // We assume that full-access users get the "contact/create" link; any user lacking this link is restricted.
    const isRestrictedUser = computed(() => {
        // If there is no "contact/create" link, then treat the user as an Anykey partner (restricted)
        return !hasLink('contact/create')
    })

    // The computed number of contacts to show.
    // For restricted users we use 5; for full-access users, we use 50.
    const contactsCount = computed(() => {
        return isRestrictedUser.value ? 5 : 50
    })

    // The search function: if restricted, always use a limit of 5 results.
    const search = () => {
        const limitVal = isRestrictedUser.value ? 5 : 20
        fetchContactsSearch({ limit: limitVal, offset: 0, filter: searchTerm.value })
    }

    // Function to select a contact and route accordingly.
    const { clearSelectedOfferStore } = useOfferStore()
    const selectContact = (contact: Offer) => {
        // Clear any existing selected offer when creating a new one
        clearSelectedOfferStore()
        router.push('/offers/createoffer/' + contact.contactId + '/0')
    }

    // Stub implementation for load (pagination not implemented here).
    const load = ({ done }: { done: Function }) => {
        console.log('Load not implemented – pagination is disabled for now.')
    }

    // Define breadcrumbs.
    const breadcrumbs = [
        { title: 'Offerten', disabled: false, href: '/offers' },
        { title: 'Kontakt auswählen', disabled: false, href: '/offers/selectcontacts' },
    ]
</script>
